import { useEffect, useMemo, useRef } from 'react';
import styled from '@emotion/styled';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectAppVersion, selectIsAuthenticated, selectSiteObject, selectUserPreferences } from '@/redux/selectors/authSelectors';
import { getSiteObject, savePreferences } from '@/redux/slices/authSlice';
import { SnackbarKey, useSnackbar } from 'notistack';
import { Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RocketIcon from '@/components/_common/Icons/RocketIcon';

type ReleaseNotesProps = {
  children: React.ReactNode;
};

const ReleaseNotes = ({ children }: ReleaseNotesProps) => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const preferences = useAppSelector(selectUserPreferences);
  const appVersion = useAppSelector(selectAppVersion);
  const site = useAppSelector(selectSiteObject);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const notifId = useRef<SnackbarKey | undefined>();

  const onUpdatePreferences = (open: boolean) => {
    if (notifId.current) {
      closeSnackbar(notifId.current);
      notifId.current = undefined;
      dispatch(
        savePreferences({
          site_updated_at: site?.updated_at,
          last_seen_version: appVersion?.polaris?.version,
        })
      );

      if (open) {
        window.open(site?.splash_content);
      }
    }
  };
  const Content = useMemo(
    () => () => (
      <>
        <SvgWrapper>
          <RocketIcon />
        </SvgWrapper>
        <Message>
          <MessageHeading>New Update Released!</MessageHeading>
          <MessageVersion>Polaris v{appVersion?.polaris?.version}</MessageVersion>
        </Message>
        <ReleaseNotesButton onClick={() => onUpdatePreferences(true)} size="small" variant="contained">
          {site && site.name.length < 35 ? site.name : 'See release notes'}
        </ReleaseNotesButton>
        <CloseButton size="small" onClick={() => onUpdatePreferences(false)}>
          <CloseIcon fontSize="small" />
        </CloseButton>
      </>
    ),
    [appVersion, site]
  );

  useEffect(() => {
    const preferencesNotEqual = !preferences?.site_updated_at ||
      !preferences?.last_seen_version ||
      preferences?.site_updated_at !== site?.updated_at ||
      preferences?.last_seen_version !== appVersion?.polaris?.version;

    if (isAuthenticated && preferences && appVersion && site && !notifId.current && !!site.splash_content && preferencesNotEqual) {
      const id = enqueueSnackbar(<Content />, {
        variant: 'releaseNotes',
        persist: true,
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'right'
        }
      });

      notifId.current = id;
    }
  }, [isAuthenticated, site, notifId, preferences, appVersion]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(getSiteObject());
    }
  }, [isAuthenticated]);

  return children;
};

export default ReleaseNotes;

const Message = styled.div`
  display: block;
`;

const MessageHeading = styled.div`
  font-size: 1.14rem;
  color: ${({ theme }) => theme.palette.text.primary};
  font-style: normal;
  font-weight: 700;
  line-height: 22px;
`;

const MessageVersion = styled.div`
  font-size: 1.14rem;
  font-style: normal;
  font-weight: 400;
  line-height: 22px;
`;

const ReleaseNotesButton = styled(Button)`
  && {
    font-size: 0.9rem;
    margin-left: ${(props) => props.theme.spacing(5)};
  }
`;

const CloseButton = styled(IconButton)`
  align-self: start;

  svg {
    opacity: 0.5;
  }
`;

const SvgWrapper = styled.div`
  display: inline-block;
  width: 40px;
  height: 40px;
  margin-right: ${(props) => props.theme.spacing(1)};

  svg {
    width: 40px;
    height: 40px;
    min-width: 40px;
    min-height: 40px;
  }
`;
